import React from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
} from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts'
import { RootState } from '../app/store'

// Generate activity data based on actual enrollment dates
const generateActivityData = (userEnrollments: any[]) => {
  // Get the last 6 months including current month
  const now = new Date()
  const months: { name: string; start: Date; end: Date }[] = []
  
  for (let i = 5; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0)
    months.push({
      name: monthDate.toLocaleDateString('en-US', { month: 'short' }),
      start: monthDate,
      end: monthEnd,
    })
  }
  
  return months.map(month => {
    // Count activities in this month based on started_at or enrolled_at dates
    const counts = { courses: 0, assessments: 0 }
    
    if (userEnrollments && Array.isArray(userEnrollments)) {
      userEnrollments.forEach((enrollment: any) => {
        // Use started_at if available, otherwise use enrolled_at
        const activityDate = enrollment.started_at 
          ? new Date(enrollment.started_at) 
          : enrollment.enrolled_at 
            ? new Date(enrollment.enrolled_at)
            : null
        
        if (!activityDate) return
        
        // Check if activity date falls within this month
        if (activityDate >= month.start && activityDate <= month.end) {
          if (enrollment.content_type === 'course') {
            counts.courses = counts.courses + 1
          } else if (enrollment.content_type === 'assessment' && enrollment.is_standalone_assessment) {
            counts.assessments = counts.assessments + 1
          }
        }
      })
    }
    
    return {
      name: month.name,
      courses: counts.courses,
      assessments: counts.assessments,
    }
  })
}

const generateProgressData = (userEnrollments: any[]) => {
  // Filter to only include courses and standalone assessments
  // Exclude course-embedded assessments to avoid double-counting
  const filteredEnrollments = (userEnrollments || []).filter(ue => {
    if (ue.content_type === 'course') {
      return true; // Include all courses
    }
    if (ue.content_type === 'assessment') {
      return ue.is_standalone_assessment === true; // Only standalone assessments
    }
    return false;
  });

  const total = filteredEnrollments.length;
  if (total === 0) {
    return [
      { name: 'Completed', value: 0, color: '#37B256' },
      { name: 'In Progress', value: 0, color: '#E96D17' },
      { name: 'Not Started', value: 0, color: '#B9B9B9' },
    ]
  }

  const completed = filteredEnrollments.filter(ue => ue.status === 'completed').length
  const inProgress = filteredEnrollments.filter(ue => ue.status === 'in_progress').length
  const notStarted = filteredEnrollments.filter(ue => ue.status === 'assigned').length

  return [
    { name: 'Completed', value: Math.round((completed / total) * 100), color: '#37B256' },
    { name: 'In Progress', value: Math.round((inProgress / total) * 100), color: '#E96D17' },
    { name: 'Not Started', value: Math.round((notStarted / total) * 100), color: '#B9B9B9' },
  ]
}

interface LearningActivityChartProps {
  data?: any[]
  height?: number
  showLegend?: boolean
}

export const LearningActivityChart: React.FC<LearningActivityChartProps> = ({
  height = 200,
  showLegend = true,
}) => {
  const theme = useTheme()
  const { user } = useSelector((state: RootState) => state.auth)
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments } = useSelector((state: RootState) => state.assignments)
  
  // Generate real data based on user enrollments
  const data = generateActivityData(userEnrollments || [])
  
  // Calculate date range for subtitle
  const now = new Date()
  const startMonth = new Date(now.getFullYear(), now.getMonth() - 5, 1)
  const dateRange = `${startMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Learning Activity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dateRange}
            </Typography>
          </Box>
          {showLegend && (
            <Box display="flex" gap={1}>
              <Chip 
                label="Courses" 
                size="small" 
                variant="outlined"
                sx={{ 
                  borderColor: theme.palette.primary.main, 
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                }}
              />
              <Chip 
                label="Assessments" 
                size="small"
                sx={{ 
                  bgcolor: theme.palette.secondary.main + '20', 
                  color: theme.palette.secondary.main,
                  fontWeight: 500,
                }}
              />
            </Box>
          )}
        </Box>
        
        <Box sx={{ height, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorCourses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorAssessments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="name" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
              />
              <Area
                type="monotone"
                dataKey="courses"
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorCourses)"
                name="Courses"
              />
              <Area
                type="monotone"
                dataKey="assessments"
                stroke={theme.palette.secondary.main}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorAssessments)"
                name="Assessments"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  )
}

export const ProgressChart: React.FC<{ size?: number }> = ({
  size = 150,
}) => {
  const { user } = useSelector((state: RootState) => state.auth)
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments } = useSelector((state: RootState) => state.assignments)
  
  // Generate real progress data based on user enrollments
  const data = generateProgressData(userEnrollments || [])

  return (
    <Card>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Progress Overview
        </Typography>
        
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <PieChart width={size} height={size}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.25}
              outerRadius={size * 0.4}
              paddingAngle={5}
              dataKey="value"
            >
              {data.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {data[0]?.value || 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" gap={1} mt={2} flexWrap="wrap">
          {data.map((item: any, index: number) => (
            <Chip
              key={index}
              label={`${item.name}: ${item.value}%`}
              size="small"
              sx={{
                bgcolor: item.color + '20',
                color: item.color,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}

export const SimpleBarChart: React.FC<{
  data?: any[]
  height?: number
  dataKey?: string
  color?: string
  title?: string
}> = ({
  data = [],
  height = 120,
  dataKey = 'value',
  color,
  title = 'Activity',
}) => {
  const theme = useTheme()
  const barColor = color || theme.palette.primary.main

  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight="bold" mb={2}>
          {title}
        </Typography>
      )}
      <Box sx={{ height, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            />
            <YAxis hide />
            <Bar 
              dataKey={dataKey} 
              fill={barColor}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}